import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatTimestamp, formatRelativeTime, formatNumber } from '@/lib/formatters';
import type { AdminDocumentRecord, AdminUserDocumentsResponse } from '@/types/admin';

export default function UserDocumentsPage() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<AdminUserDocumentsResponse | null>(null);
  const [allDocs, setAllDocs] = useState<AdminDocumentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastKey, setLastKey] = useState<Record<string, unknown> | undefined>();
  const [projectFilter, setProjectFilter] = useState(searchParams.get('projectId') ?? '');

  const fetchDocs = async (key?: Record<string, unknown>, append = false, pid?: string) => {
    if (!userId) return;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: '25' });
      if (key) params.set('lastKey', JSON.stringify(key));
      const effectivePid = pid !== undefined ? pid : projectFilter;
      if (effectivePid) params.set('projectId', effectivePid);

      const res = await authenticatedFetch(`users/${userId}/documents?${params}`);
      if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
      const payload = (await res.json()) as AdminUserDocumentsResponse;

      if (append) {
        setAllDocs((prev) => [...prev, ...payload.documents]);
      } else {
        setAllDocs(payload.documents);
      }
      setData(payload);
      setLastKey(payload.lastKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load documents');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => {
    setLastKey(undefined);
    fetchDocs(undefined, false, projectFilter);
  };

  const handleClearFilter = () => {
    setProjectFilter('');
    setLastKey(undefined);
    fetchDocs(undefined, false, '');
  };

  return (
    <>
      <LoadingOverlay visible={loading} message="Loading documents..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Documents' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/users/${userId}/projects`}>Projects →</Link>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter by project ID"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
            className="max-w-xs font-mono text-xs"
          />
          <Button variant="outline" size="sm" onClick={handleFilter} disabled={loading}>
            Filter
          </Button>
          {projectFilter && (
            <Button variant="ghost" size="sm" onClick={handleClearFilter}>
              Clear
            </Button>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Documents (snippets-table)</CardTitle>
            <CardDescription>
              {data
                ? `${allDocs.length} documents loaded${data.hasMore ? ' — more available' : ''}`
                : 'User writing documents'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {allDocs.length > 0 ? (
              <>
                <div className="overflow-hidden rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document ID</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead className="text-right">Size</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Updated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allDocs.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-mono text-xs">{doc.id}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">{doc.title}</span>
                              {doc.summary && (
                                <span className="text-xs text-muted-foreground line-clamp-1">{doc.summary}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {doc.docType && <Badge variant="outline" className="w-fit">{doc.docType}</Badge>}
                              {doc.genre && <span className="text-xs text-muted-foreground">{doc.genre}</span>}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{doc.projectId ?? '—'}</TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {doc.kb != null ? `${formatNumber(doc.kb)} KB` : '—'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {doc.created ? formatTimestamp(doc.created) : '—'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {doc.updated ? (
                              <span title={formatTimestamp(doc.updated)}>{formatRelativeTime(doc.updated)}</span>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {data?.hasMore && (
                  <div className="mt-3">
                    <Button variant="outline" size="sm" onClick={() => fetchDocs(lastKey, true)} disabled={loadingMore}>
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              !loading && <p className="text-sm text-muted-foreground">No documents found.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
